import { isEmpty, isNotEmpty, sanitizeValues } from '@intelligence/v3/utils/common';
import { handleAllGameFilter } from '@intelligence/utils/topchartsUtils';
import {
  getDefaultApiFieldMap,
  getDefaultUrlFieldMap,
  getItemApiParamsFieldMap,
  getItemUrlParamsFieldMap,
} from '../shared/fieldMapUtils';
import { getGameSelectValue } from './utils';

import type { IFilterMiddlewareUtils } from '../shared/types';
import type { IFilterItemConfig } from '@intelligence/v3/filterCore/types';
import type { IGameSelectFilterValue } from './types';

/**
 * 游戏选择筛选器的 API 入参类型
 * checkedList 和 UI 状态字段不传入 API
 */
interface IGameSelectApiParams {
  [key: string]: any;
  keywords?: string;
  isSearchAll?: boolean;
}

const VALUE_FIELD = 'filter_ids';
const URL_VALUE_FIELD = 'gameIds';
const KEYWORDS_FIELD = 'keywords';
const SEARCH_ALL_FIELD = 'isSearchAll';
const ENTITY_TYPES_IDS_OBJ_FIELD = 'entityTypesIdsObj';

const getApiFieldMap = (
  item: IFilterItemConfig,
  params?: unknown,
): {
  entityTypesIdsObjField: string;
  isSearchAllField: string;
  keywordsField: string;
  valueField: string;
} => {
  const apiParamsFieldMap = getItemApiParamsFieldMap(item, params);
  const defaultApiFieldMap = getDefaultApiFieldMap(params);

  return {
    entityTypesIdsObjField:
      apiParamsFieldMap?.entityTypesIdsObj || defaultApiFieldMap.entityTypesIdsObj || ENTITY_TYPES_IDS_OBJ_FIELD,
    isSearchAllField: apiParamsFieldMap?.isSearchAll || defaultApiFieldMap.isSearchAll || SEARCH_ALL_FIELD,
    keywordsField: apiParamsFieldMap?.keywords || defaultApiFieldMap.keywords || KEYWORDS_FIELD,
    valueField: apiParamsFieldMap?.value || defaultApiFieldMap.value || VALUE_FIELD,
  };
};

const getUrlFieldMap = (
  item: IFilterItemConfig,
  params?: unknown,
): {
  isSearchAllField: string;
  keywordsField: string;
  valueField: string;
} => {
  const urlParamsFieldMap = getItemUrlParamsFieldMap(item, params);
  const defaultUrlFieldMap = getDefaultUrlFieldMap(params);

  return {
    isSearchAllField: urlParamsFieldMap?.isSearchAll || defaultUrlFieldMap.isSearchAll || SEARCH_ALL_FIELD,
    keywordsField: urlParamsFieldMap?.keywords || defaultUrlFieldMap.keywords || KEYWORDS_FIELD,
    valueField: urlParamsFieldMap?.value || defaultUrlFieldMap.value || URL_VALUE_FIELD,
  };
};

const getSearchAllKeywords = (filterValue?: IGameSelectFilterValue): string => {
  return getGameSelectValue(filterValue).keywords || '';
};

const shouldKeepKeywordSearchAll = (filterValue?: IGameSelectFilterValue): boolean => {
  return filterValue?.isSearchAll === true && isNotEmpty(getSearchAllKeywords(filterValue));
};

const getSerializableGameSelectValue = (
  filterValue?: IGameSelectFilterValue,
): {
  normalizedValue: IGameSelectFilterValue;
  unique: string[];
  keepKeywordSearchAll: boolean;
} | null => {
  const normalizedValue = getGameSelectValue(filterValue);
  const unique = sanitizeValues(normalizedValue.value);
  const keepKeywordSearchAll = shouldKeepKeywordSearchAll(normalizedValue);

  if (isEmpty(unique) && !keepKeywordSearchAll) {
    return null;
  }

  return {
    normalizedValue,
    unique,
    keepKeywordSearchAll,
  };
};

/**
 * 游戏选择筛选器中间件工具
 * value 类型：IGameSelectFilterValue
 *   { value: string[]; keywords?: string;
 *     isSearchAll?: boolean; checkedList?: any[] }
 *
 * 校验规则：
 * - value 必须为非空字符串数组，自动去重
 * - keywords 为可选非空字符串
 * - isSearchAll 为可选布尔值
 * - checkedList 仅为 UI 状态，不参与序列化
 */
class GameSelectMiddlewareUtils implements IFilterMiddlewareUtils<IGameSelectFilterValue, IGameSelectApiParams> {
  convertToApiParams(
    item: IFilterItemConfig,
    filterValue?: IGameSelectFilterValue,
    params?: unknown,
  ): IGameSelectApiParams | null {
    const serializableValue = getSerializableGameSelectValue(filterValue);

    if (!serializableValue) {
      return null;
    }

    const { keepKeywordSearchAll, normalizedValue, unique } = serializableValue;
    const { checkedList } = normalizedValue;
    const { entityTypesIdsObjField, isSearchAllField, keywordsField, valueField } = getApiFieldMap(item, params);
    const result: IGameSelectApiParams = {
      [valueField]: keepKeywordSearchAll ? [] : unique,
    };

    if (keepKeywordSearchAll) {
      result[keywordsField] = normalizedValue.keywords;
      result[isSearchAllField] = true;
    }
    if (isNotEmpty(checkedList)) {
      result[entityTypesIdsObjField] = handleAllGameFilter(checkedList || []);
    }

    return result;
  }

  getManagedUrlKeys(item: IFilterItemConfig, params?: unknown): string[] {
    const { isSearchAllField, keywordsField, valueField } = getUrlFieldMap(item, params);

    return [valueField, keywordsField, isSearchAllField].filter(isNotEmpty);
  }

  serializeUrlParams(
    item: IFilterItemConfig,
    filterValue?: IGameSelectFilterValue,
    params?: unknown,
  ): IRecord<string> | null {
    const serializableValue = getSerializableGameSelectValue(filterValue);

    if (!serializableValue) {
      return null;
    }

    const { keepKeywordSearchAll, normalizedValue, unique } = serializableValue;
    const { isSearchAllField, keywordsField, valueField } = getUrlFieldMap(item, params);
    const result: IRecord<string> = {};

    if (!keepKeywordSearchAll) {
      result[valueField] = unique.join(',');
      return result;
    }

    result[keywordsField] = normalizedValue?.keywords || '';
    result[isSearchAllField] = 'true';

    return result;
  }

  deserializeUrlParams(
    item: IFilterItemConfig,
    searchParams: URLSearchParams,
    params?: unknown,
  ): IGameSelectFilterValue | null {
    if (isEmpty(searchParams)) {
      return null;
    }

    const { isSearchAllField, keywordsField, valueField } = getUrlFieldMap(item, params);
    const unique = sanitizeValues((searchParams.get(valueField) || '').split(',').map((itemValue) => itemValue.trim()));
    const keywords = searchParams.get(keywordsField) || '';
    const isSearchAll = searchParams.get(isSearchAllField);
    const keepKeywordSearchAll = isSearchAll === 'true' && isNotEmpty(keywords.trim());

    if (isEmpty(unique) && !keepKeywordSearchAll) {
      return null;
    }

    const result: IGameSelectFilterValue = {
      value: unique,
      checkedList: [],
    };

    if (keepKeywordSearchAll) {
      result.keywords = keywords.trim();
      result.isSearchAll = true;
    }

    return getGameSelectValue(result);
  }
}

export default GameSelectMiddlewareUtils;
